import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useAddressSuggestions, useCreateAddress } from "./hooks";

interface AddressAutocompleteProps {
  name: string;
  value?: number | string;
  displayValue?: string;
  endpoint: string;
  placeholder?: string;
}

export function AddressAutocomplete({
  name,
  value,
  displayValue,
  endpoint,
  placeholder = "Rechercher une adresse...",
}: AddressAutocompleteProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<any>(
    displayValue ? { id: value, formatted_address: displayValue } : null,
  );

  const { data: suggestions = [], isLoading } = useAddressSuggestions(
    debouncedQuery,
    debouncedQuery.length >= 3,
  );

  const createAddress = useCreateAddress();

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Show suggestions when we have data
  useEffect(() => {
    if (suggestions.length > 0 && query.length >= 3) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [suggestions, query]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const selectPlace = (place: any) => {
    createAddress.mutate(
      { endpoint, placeId: place.place_id },
      {
        onSuccess: (data) => {
          setSelectedAddress(data);
          setQuery("");
          setShowSuggestions(false);
        },
      },
    );
  };

  const clearAddress = () => {
    setSelectedAddress(null);
    setQuery("");
  };

  return (
    <div className="relative">
      {selectedAddress ? (
        <div className="flex justify-between items-center h-[40px] bg-white rounded-lg px-4 border border-slate-300">
          <span className="text-sm truncate">
            {selectedAddress.formatted_address || selectedAddress.short_name}
          </span>
          <button
            type="button"
            onClick={clearAddress}
            className="ml-2 text-slate-500 hover:text-slate-700"
          >
            <X className="w-4 h-4" />
          </button>
          <input type="hidden" name={name} value={selectedAddress.id} />
        </div>
      ) : (
        <>
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-default-600"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute w-full bg-white shadow-lg rounded-md mt-1 z-50 border border-slate-200">
              {suggestions.map((place, index) => (
                <div
                  key={index}
                  onClick={() => selectPlace(place)}
                  className="p-2 hover:bg-slate-100 cursor-pointer text-xs"
                >
                  {place.description}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
